import { getWeapon, type WeaponSpec, type WeaponClass } from "@hupper/shared/weapons";

/**
 * Inline-SVG HUD iconography. Everything returns a markup string so the HUD
 * can drop icons straight into `innerHTML` — no image assets, matching the
 * existing all-SVG vitals (shield / helmet glyphs).
 *
 * Two visual conventions, mirroring CS:GO:
 *   • Weapons   — monochrome silhouettes using `currentColor`, so the HUD
 *                 tints them per state (equipped = bright/gold, else dim).
 *                 Each weapon is a hand-built left-facing silhouette with
 *                 real proportions (barrel, receiver, magazine, grip, stock).
 *   • Grenades  — color-coded by type (the color IS the identity), so they
 *                 read at a glance even when not the active slot.
 *
 * Per-weapon art keeps its own viewBox / aspect ratio (a pistol is chunky,
 * a rifle long, the AWP very long); the HUD sizes by height and lets width
 * follow, so longer guns render wider — exactly like the reference.
 */

type Art = { vb: string; inner: string };

/* ── Weapon silhouettes (left-facing, muzzle to the left) ─────────────── */

// Handgun — Glock / USP / Dual Berettas. Slide + sights + muzzle, with a
// one-piece frame whose trigger guard is cut as a hole (evenodd).
const PISTOL: Art = {
  vb: "0 0 70 52",
  inner: `
    <rect x="0" y="12" width="5" height="5" rx="1.2"/>
    <rect x="6.5" y="7.4" width="3" height="3" rx="0.6"/>
    <rect x="40" y="6.4" width="7" height="4" rx="1"/>
    <path d="M4 10 H47 a3.5 3.5 0 0 1 3.4 2.6 L51.6 17.6 H7 a3 3 0 0 1 -3 -3 Z"/>
    <path fill-rule="evenodd" d="M7 18.6 H53.5 a3 3 0 0 1 3 3.3 L54.2 48 a3.4 3.4 0 0 1 -3.4 3.2 H42.4
      a3.4 3.4 0 0 1 -3.3 -2.7 L36 31.6 H32.6 a10.6 10.6 0 0 1 -9.9 -7.2 H10 a3 3 0 0 1 -3 -3 Z
      M21 25 a6.9 6.9 0 0 0 6.3 5.2 H35 L33.8 25 Z"/>`,
};

const USP_S: Art = {
  vb: "0 0 90 52",
  inner: `
    <!-- Silencer -->
    <rect x="0" y="10" width="28" height="7" rx="1.5"/>
    <rect x="2" y="11" width="24" height="5" fill="none" stroke="currentColor" stroke-width="0.5" opacity="0.5" stroke-dasharray="1 2"/>
    <!-- Sights -->
    <rect x="32" y="7.4" width="3" height="3" rx="0.6"/>
    <rect x="58" y="6.4" width="7" height="4" rx="1"/>
    <!-- Slide -->
    <path d="M29 10 H65 a3.5 3.5 0 0 1 3.4 2.6 L69.6 17.6 H32 a3 3 0 0 1 -3 -3 Z"/>
    <!-- Frame & Grip -->
    <path fill-rule="evenodd" d="M32 18.6 H71.5 a3 3 0 0 1 3 3.3 L72.2 48 a3.4 3.4 0 0 1 -3.4 3.2 H60.4
      a3.4 3.4 0 0 1 -3.3 -2.7 L54 31.6 H50.6 a10.6 10.6 0 0 1 -9.9 -7.2 H29 a3 3 0 0 1 -3 -3 Z
      M39 25 a6.9 6.9 0 0 0 6.3 5.2 H53 L51.8 25 Z"/>`,
};

const DUALIES: Art = {
  vb: "0 0 80 52",
  inner: `
    <!-- Back Pistol (dimmed) -->
    <g opacity="0.4">
      <rect x="0" y="7" width="5" height="4" rx="1"/>
      <rect x="6.5" y="3.4" width="3" height="3" rx="0.6"/>
      <rect x="35" y="2.4" width="6" height="3" rx="1"/>
      <path d="M4 6 H42 a3 3 0 0 1 3 2 L46 12 H7 a2 2 0 0 1 -2 -2 Z"/>
      <path fill-rule="evenodd" d="M7 13 H48 a2 2 0 0 1 2 2.5 L48.5 38 a2.5 2.5 0 0 1 -2.5 2.5 H38
        a2.5 2.5 0 0 1 -2.5 -2 L33 24 H30 a8 8 0 0 1 -8 -6 H10 a2 2 0 0 1 -2 -2 Z
        M18 18 a5 5 0 0 0 5 4 H32 L31 18 Z"/>
    </g>
    <!-- Front Pistol -->
    <g transform="translate(10, 8)">
      <rect x="0" y="12" width="5" height="5" rx="1.2"/>
      <rect x="6.5" y="7.4" width="3" height="3" rx="0.6"/>
      <rect x="40" y="6.4" width="7" height="4" rx="1"/>
      <path d="M4 10 H47 a3.5 3.5 0 0 1 3.4 2.6 L51.6 17.6 H7 a3 3 0 0 1 -3 -3 Z"/>
      <path fill-rule="evenodd" d="M7 18.6 H53.5 a3 3 0 0 1 3 3.3 L54.2 48 a3.4 3.4 0 0 1 -3.4 3.2 H42.4
        a3.4 3.4 0 0 1 -3.3 -2.7 L36 31.6 H32.6 a10.6 10.6 0 0 1 -9.9 -7.2 H10 a3 3 0 0 1 -3 -3 Z
        M21 25 a6.9 6.9 0 0 0 6.3 5.2 H35 L33.8 25 Z"/>
    </g>`,
};

// Desert Eagle — chunkier slide, fatter barrel, beefier grip.
const DEAGLE: Art = {
  vb: "0 0 76 52",
  inner: `
    <rect x="0" y="11.5" width="7" height="6.5" rx="1.4"/>
    <rect x="9" y="6.8" width="3.4" height="3.4" rx="0.6"/>
    <rect x="44" y="5.6" width="8" height="4.4" rx="1"/>
    <path d="M5 9.4 H52 a4 4 0 0 1 3.8 2.8 L57 18.6 H8 a3 3 0 0 1 -3 -3 Z"/>
    <path fill-rule="evenodd" d="M8 19.6 H58 a3 3 0 0 1 3 3.4 L58.6 47 a3.6 3.6 0 0 1 -3.6 3.4 H45.6
      a3.6 3.6 0 0 1 -3.5 -2.9 L39 32.6 H34.4 a11.4 11.4 0 0 1 -10.6 -7.6 H11 a3 3 0 0 1 -3 -3 Z
      M22.4 26 a7.4 7.4 0 0 0 6.8 5.6 H38 L36.7 26 Z"/>`,
};

// MAC-10 — boxy receiver, short barrel, vertical magazine, skeleton wire stock.
const MAC10: Art = {
  vb: "0 0 80 54",
  inner: `
    <!-- Barrel -->
    <rect x="4" y="16" width="12" height="4" rx="1"/>
    <!-- Front Strap (Hanging) -->
    <path d="M 18 26 L 14 42 L 20 42 L 22 26 Z"/>
    <!-- Receiver -->
    <rect x="16" y="10" width="38" height="16" rx="2"/>
    <!-- Charging Handle (Top) -->
    <rect x="30" y="5" width="6" height="5" rx="1"/>
    <!-- Sights -->
    <rect x="18" y="6" width="3" height="4"/>
    <rect x="48" y="5" width="4" height="5"/>
    <!-- Trigger & Guard -->
    <path d="M 32 26 H 40 V 28 H 34 V 34 H 40 V 36 H 32 Z"/>
    <rect x="36" y="28" width="2" height="4"/>
    <!-- Grip & Magwell -->
    <rect x="40" y="26" width="12" height="12"/>
    <!-- Magazine -->
    <rect x="42" y="38" width="8" height="16" rx="1"/>
    <!-- Wire Stock (Collapsed) -->
    <rect x="54" y="18" width="12" height="2"/>
    <rect x="64" y="14" width="2" height="10"/>`,
};

// MP9 — angled grip, integrated sight, folding stock.
const MP9: Art = {
  vb: "0 0 106 48",
  inner: `
    <!-- Barrel/Muzzle -->
    <rect x="2" y="15" width="14" height="6" rx="1.5"/>
    <!-- Receiver -->
    <path d="M14 11 H58 a3 3 0 0 1 3 3 V24 a3 3 0 0 1 -3 3 H14 a3 3 0 0 1 -3 -3 V14 a3 3 0 0 1 3 -3 Z"/>
    <!-- Top Rail -->
    <rect x="20" y="7" width="34" height="4" rx="1"/>
    <rect x="24" y="4" width="4" height="3" rx="1"/>
    <!-- Red Dot Sight -->
    <path d="M42 4 L 46 2 L 52 2 L 54 4 V 7 H 42 Z"/>
    <circle cx="48" cy="4.5" r="1.5" fill="none" stroke="currentColor" stroke-width="0.5"/>
    <!-- Integrated Front Grip -->
    <path d="M18 24 H30 L26 42 Q25 44 22 44 H16 Q14 44 15 42 Z"/>
    <!-- Magazine -->
    <path d="M40 24 H52 L49 44 Q48.5 46 45 46 H39 Q36.5 46 37 44 Z"/>
    <rect x="42" y="27" width="6" height="17" rx="1" fill="none" stroke="currentColor" stroke-width="1.5" opacity="0.5"/>
    <!-- Folded Stock (Overlay) -->
    <path d="M58 16 H80" stroke="currentColor" stroke-width="3" stroke-linecap="round" opacity="0.6"/>
    <path d="M80 14 V26" stroke="currentColor" stroke-width="3" stroke-linecap="round" opacity="0.6"/>`,
};

// AK-47 — front sight post + gas block, slab receiver, the iconic curved
// banana magazine, rear pistol grip, fixed stock.
const AK47: Art = {
  vb: "0 0 134 48",
  inner: `
    <rect x="0" y="14" width="8" height="6" rx="1.2"/>
    <rect x="6" y="15.4" width="46" height="4.2" rx="1"/>
    <path d="M10 8.5 H14 L15.2 15.4 H8.8 Z"/>
    <rect x="24" y="10.5" width="8" height="5.4" rx="1"/>
    <rect x="45" y="8.6" width="8" height="3.6" rx="1"/>
    <path d="M50 9.6 H92 a2.4 2.4 0 0 1 2.4 2.4 V24 a2.4 2.4 0 0 1 -2.4 2.4 H50 a2.4 2.4 0 0 1 -2.4 -2.4 V12 a2.4 2.4 0 0 1 2.4 -2.4 Z"/>
    <path d="M56 26.4 H77 L73.6 37 Q71.4 43 63.6 44.4 Q58 45.4 56.8 40 L54.4 28.6 Q54 26.4 56 26.4 Z"/>
    <path d="M83 26.4 H96 L98 40 Q98 43 95 43 H87 Q84 43 84 40 Z"/>
    <path d="M92 11.6 H128 a3.4 3.4 0 0 1 3.4 3.4 V21 a3.4 3.4 0 0 1 -3.4 3.4 H99 L92 22 Z"/>`,
};

// M4A4 — flash hider, A-frame front sight, flat-top receiver with
// carry-handle rail, near-straight STANAG mag, buffer tube + telescoping stock.
const M4A4: Art = {
  vb: "0 0 132 48",
  inner: `
    <!-- Barrel & Flash Hider -->
    <rect x="0" y="15" width="7" height="5.4" rx="1.2"/>
    <rect x="6" y="16" width="40" height="3.8" rx="1"/>
    <!-- Front Sight -->
    <path d="M30 7.5 L34.5 7.5 L36.5 16 L28 16 Z"/>
    <!-- Receiver -->
    <rect x="40" y="9" width="6.5" height="4.4" rx="1"/>
    <path d="M46 10 H87 a2.4 2.4 0 0 1 2.4 2.4 V24 a2.4 2.4 0 0 1 -2.4 2.4 H46 a2.4 2.4 0 0 1 -2.4 -2.4 V12.4 a2.4 2.4 0 0 1 2.4 -2.4 Z"/>
    <!-- Top Rail -->
    <rect x="50" y="6.6" width="35" height="4" rx="1.6"/>
    <!-- Grip -->
    <path d="M54 26.8 H73 L72.2 41 Q72.2 43.2 69.2 43.2 H57.8 Q54.8 43.2 54.8 41 L54 28.8 Z"/>
    <!-- Magazine -->
    <path d="M80 26.8 H92 L94 40 Q94 43 91 43 H84 Q81 43 81 40 Z"/>
    <path d="M83 28 C83 35 84 40 87 40" fill="none" stroke="currentColor" stroke-width="1.5" opacity="0.4"/>
    <!-- Stock -->
    <rect x="86" y="12.4" width="11" height="10" rx="2.4"/>
    <path d="M95 13.4 H126 a3.4 3.4 0 0 1 3.4 3.4 V20.6 a3.4 3.4 0 0 1 -3.4 3.4 H95 Z"/>
    <rect x="123" y="10.6" width="5.5" height="15" rx="2.2"/>
    <path d="M100 24 L120 24 L123 20" fill="none" stroke="currentColor" stroke-width="2"/>`,
};

// M4A1-S — long silencer, no front sight post, curved mag, different stock.
const M4A1_S: Art = {
  vb: "0 0 150 48",
  inner: `
    <!-- Silencer -->
    <rect x="0" y="14" width="32" height="7.5" rx="1.5"/>
    <rect x="4" y="15" width="24" height="5.5" fill="none" stroke="currentColor" stroke-width="0.5" opacity="0.5" stroke-dasharray="1 2"/>
    <!-- Barrel -->
    <rect x="30" y="16" width="20" height="3.8" rx="1"/>
    <!-- Receiver -->
    <rect x="48" y="9" width="6.5" height="4.4" rx="1"/>
    <path d="M54 10 H95 a2.4 2.4 0 0 1 2.4 2.4 V24 a2.4 2.4 0 0 1 -2.4 2.4 H54 a2.4 2.4 0 0 1 -2.4 -2.4 V12.4 a2.4 2.4 0 0 1 2.4 -2.4 Z"/>
    <!-- Top Rail -->
    <rect x="58" y="6.6" width="35" height="4" rx="1.6"/>
    <!-- Grip -->
    <path d="M62 26.8 H81 L80.2 41 Q80.2 43.2 77.2 43.2 H65.8 Q62.8 43.2 62.8 41 L62 28.8 Z"/>
    <!-- Magazine (Curved like STANAG) -->
    <path d="M88 26.8 H100 L103 40 Q104 43 100 43 H92 Q89 43 88 40 Z"/>
    <path d="M91 28 C91 35 92 40 96 40" fill="none" stroke="currentColor" stroke-width="1.5" opacity="0.4"/>
    <!-- Stock -->
    <rect x="94" y="12.4" width="11" height="10" rx="2.4"/>
    <path d="M103 13.4 H138 a3.4 3.4 0 0 1 3.4 3.4 V20.6 a3.4 3.4 0 0 1 -3.4 3.4 H103 Z"/>
    <rect x="135" y="10.6" width="7" height="18" rx="3.5"/>`,
};

// AWP — long muzzle brake + barrel, big scope on tall mounts, bolt handle,
// magazine, pistol grip, skeletonised thumbhole stock (thumbhole cut out).
const AWP: Art = {
  vb: "0 0 160 44",
  inner: `
    <rect x="0" y="17" width="15" height="6.4" rx="1.6"/>
    <rect x="13" y="18.6" width="50" height="3.8" rx="1"/>
    <path d="M58 15 H105 a2.4 2.4 0 0 1 2.4 2.4 V26 a2.4 2.4 0 0 1 -2.4 2.4 H58 a2.4 2.4 0 0 1 -2.4 -2.4 V17.4 a2.4 2.4 0 0 1 2.4 -2.4 Z"/>
    <rect x="60" y="4.5" width="46" height="8.5" rx="4.25"/>
    <rect x="66" y="12.5" width="4.5" height="3.5"/>
    <rect x="95" y="12.5" width="4.5" height="3.5"/>
    <path d="M100 15.5 L108 9.5 L109.8 12 L103.5 17 Z"/>
    <rect x="79" y="28" width="15" height="9.5" rx="1.6"/>
    <path d="M105 28 H117 L119 40 Q119 42.4 116 42.4 H109 Q106 42.4 106 40 Z"/>
    <path fill-rule="evenodd" d="M105 15.5 H153 a4.4 4.4 0 0 1 4.4 4.4 L155.2 31 a4.4 4.4 0 0 1 -4.4 3.6 H121.4 L117 28 H111 L105 22.6 Z
      M135 20 a6.4 6.4 0 1 0 0.01 0 Z"/>`,
};

// Combat knife — clip-point blade (tip left), bolster, contoured handle with
// a lanyard hole (cut out).
const KNIFE: Art = {
  vb: "0 0 88 32",
  inner: `
    <!-- Blade -->
    <path d="M2 17.5 Q13 11.5 24 10.6 Q21 13.6 16.5 15.2 Q28 13 45 10.4 L53 10 L53 23.4 Q32 25.4 15 22.2 Q7 20.8 2 17.5 Z"/>
    <path d="M10 16 Q20 14 30 14" fill="none" stroke="#000" stroke-width="0.5" opacity="0.5"/>
    <!-- Guard/Bolster -->
    <rect x="50.5" y="7.5" width="6" height="18.5" rx="1.6"/>
    <!-- Handle -->
    <path fill-rule="evenodd" d="M56 9.6 H81 Q85.6 9.6 85.6 14.2 V19 Q85.6 23.6 81 23.6 H56 Z
      M77.5 13.6 a2.1 2.1 0 1 0 0.01 0 Z"/>
    <!-- Handle texture -->
    <path d="M60 12 V21 M65 12 V21 M70 12 V21" fill="none" stroke="#000" stroke-width="1" opacity="0.3"/>`,
};

const WEAPON_ART_BY_ID: Record<string, Art> = {
  g18: PISTOL,
  usp_s: USP_S,
  dualies: DUALIES,
  deagle: DEAGLE,
  mac10: MAC10,
  mp9: MP9,
  ak47: AK47,
  m4a4: M4A4,
  m4a1_s: M4A1_S,
  awp: AWP,
  knife: KNIFE,
};

const WEAPON_ART_BY_CLASS: Record<WeaponClass, Art> = {
  pistol: PISTOL,
  smg: MAC10,
  rifle: AK47,
  sniper: AWP,
  melee: KNIFE,
  grenade: { vb: "0 0 32 32", inner: `<circle cx="16" cy="16" r="8"/>` },
};

/**
 * Rendered PNG silhouettes (in /public/weapons) for the marquee weapons — they
 * read richer than the hand-built SVG at HUD size. Pre-trimmed, transparent,
 * and tinted to the HUD's warm off-white so CSS only has to dim via opacity.
 * Any id not listed here falls back to the SVG art above.
 */
const WEAPON_IMG_BY_ID: Record<string, string> = {
  ak47: "/weapons/ak47.png",
  m4a1_s: "/weapons/m4a1_s.png",
  m4a4: "/weapons/m4a4.png",
  awp: "/weapons/awp.png",
  g18: "/weapons/g18.png",
  deagle: "/weapons/deagle.png",
  usp_s: "/weapons/usp_s.png",
  dualies: "/weapons/dualies.png",
  mac10: "/weapons/mac10.png",
  mp9: "/weapons/mp9.png",
  knife: "/weapons/knife.png",
};

/** Every rendered-PNG icon url — warmed into the browser cache by the
 *  match-start load gate so HUD / kill-feed icons never pop in mid-round. */
export const WEAPON_ICON_IMG_URLS: ReadonlyArray<string> = Object.values(WEAPON_IMG_BY_ID);

/** Weapon HUD icon — a rendered PNG when we ship one, else an SVG silhouette. */
export function weaponIconSvg(spec: WeaponSpec): string {
  const img = WEAPON_IMG_BY_ID[spec.id];
  if (img) {
    return `<img class="hud-wic-img" src="${img}" alt="" aria-hidden="true" draggable="false" />`;
  }
  const art = WEAPON_ART_BY_ID[spec.id] ?? WEAPON_ART_BY_CLASS[spec.class] ?? PISTOL;
  return `<svg class="hud-wic" viewBox="${art.vb}" fill="currentColor" aria-hidden="true">${art.inner}</svg>`;
}

/**
 * Kill-feed weapon icon — same art as the HUD. Guns / knife use the warm-white
 * silhouette (rendered PNG when we ship one, else SVG); a grenade kill (id like
 * `he` / `molotov`) uses the color-coded grenade glyph. Unknown ids → empty.
 */
export function killWeaponIcon(weaponId: string): string {
  let spec: WeaponSpec | null = null;
  try { spec = getWeapon(weaponId); } catch { spec = null; }
  if (spec?.class === "grenade") return grenadeIconSvg(weaponId);
  if (spec) return weaponIconSvg(spec);
  return "";
}

/**
 * Kill-feed modifier badges (warm-white PNGs in /public/killfeed), matching the
 * weapon-icon tint. `headshot` = brain shot, `smoke` = victim died inside a
 * smoke cloud, `blind` = victim was flash-blinded at death.
 */
export type KillBadge = "headshot" | "smoke" | "blind" | "noscope";

const KILL_BADGE_IMG: Record<KillBadge, string> = {
  headshot: "/killfeed/headshot.png",
  smoke: "/killfeed/smoke.png",
  blind: "/killfeed/blind.png",
  noscope: "/killfeed/noscope.png",
};

export function killBadgeImg(kind: KillBadge): string {
  return `<img class="kf-badge-img kf-badge-${kind}" src="${KILL_BADGE_IMG[kind]}" alt="" aria-hidden="true" draggable="false" />`;
}

/* ── Grenades — color-coded, self-contained fills ─────────────────────── */

const GRENADE_SVGS: Record<string, string> = {
  // HE — olive fragmentation egg with cap + safety lever.
  he: `
    <!-- Pin ring -->
    <circle cx="21" cy="6" r="3" fill="none" stroke="#aab06a" stroke-width="1.5"/>
    <!-- Body -->
    <ellipse cx="16" cy="20" rx="8" ry="9" fill="#6f7d39"/>
    <!-- Fragmentation grid -->
    <path d="M10 16 H22 M10 20 H22 M10 24 H22 M13 13 V27 M16 12 V28 M19 13 V27" stroke="#3f4a1f" stroke-width="1" opacity="0.7"/>
    <!-- Cap -->
    <rect x="12" y="6" width="8" height="5" rx="1" fill="#48531f"/>
    <!-- Safety lever -->
    <path d="M18 5 H25 V7 H20 L22 18 H19 L18 8 Z" fill="#aab06a"/>`,
  
  // Flashbang — slim grey canister with a bright blue band.
  flash: `
    <!-- Pin ring -->
    <circle cx="21" cy="5" r="3" fill="none" stroke="#9aa0a8" stroke-width="1.5"/>
    <!-- Body -->
    <rect x="11" y="9" width="10" height="19" rx="3.5" fill="#c2c8cf"/>
    <rect x="11" y="15" width="10" height="3.5" fill="#5aa6e8"/>
    <!-- Cap -->
    <rect x="13" y="5" width="6" height="5" rx="1" fill="#7d848c"/>
    <!-- Safety lever -->
    <path d="M17 4 H23 V6 H19 L21 15 H18 L17 7 Z" fill="#9aa0a8"/>`,
  
  // Smoke — fatter green canister with a band.
  smoke: `
    <!-- Pin ring -->
    <circle cx="21" cy="5" r="3" fill="none" stroke="#8fb56b" stroke-width="1.5"/>
    <!-- Body -->
    <rect x="9" y="9" width="14" height="19" rx="4.5" fill="#6f9a4e"/>
    <rect x="9" y="16" width="14" height="3.5" fill="#3f5b2a"/>
    <!-- Cap -->
    <rect x="13" y="5" width="6" height="5" rx="1" fill="#46632f"/>
    <!-- Safety lever -->
    <path d="M17 4 H23 V6 H19 L21 15 H18 L17 7 Z" fill="#8fb56b"/>`,
  
  // Molotov — bottle with a flame.
  molotov: `
    <!-- Flame -->
    <path d="M14.5 6 C17.5 2 15.5 1 15.5 1 C14.5 6 11.5 6 11.5 6 C13 8 16 6 14.5 6 Z" fill="#f6c45e"/>
    <path d="M14 4 H22 L24 8 V8 H12 L14 4 Z" fill="#f2a33a"/>
    <!-- Rag -->
    <rect x="14" y="8" width="4" height="4" fill="#9c3a24"/>
    <!-- Bottle -->
    <rect x="9" y="11" width="14" height="17" rx="5" fill="#b8432a"/>
    <rect x="11.5" y="16" width="9" height="7" rx="2" fill="#d98f4e" opacity="0.45"/>`,
  
  // Incendiary — red canister with a flame mark.
  incendiary: `
    <!-- Pin ring -->
    <circle cx="21" cy="5" r="3" fill="none" stroke="#f2a33a" stroke-width="1.5"/>
    <!-- Flame mark -->
    <path d="M16 3 C19 7 17 8.5 17 8.5 C16 10 14 6 14 6 C15.5 8 18 6 16 3 Z" fill="#f2a33a"/>
    <!-- Body -->
    <rect x="9" y="10" width="14" height="18" rx="4.5" fill="#c2502a"/>
    <rect x="9" y="17" width="14" height="3.5" fill="#7e3019" opacity="0.7"/>
    <!-- Cap -->
    <rect x="13" y="6.5" width="6" height="4" rx="1" fill="#7e3019"/>
    <!-- Safety lever -->
    <path d="M17 5.5 H23 V7.5 H19 L21 16 H18 L17 8.5 Z" fill="#f2a33a"/>`,
};

/**
 * Display + numbering order for the utility row. Molotov (T) and Incendiary
 * (CT) share the "fire" slot, so a player only ever owns one of them.
 */
export const GRENADE_ORDER = ["he", "flash", "smoke", "molotov", "incendiary"] as const;

/**
 * Rendered PNG silhouettes (in /public/grenades) for the utility row. Warm-white
 * like the weapon icons; the HUD dims the unequipped ones. Any id not listed
 * here falls back to the color-coded SVG above.
 */
const GRENADE_IMG_BY_ID: Record<string, string> = {
  he: "/grenades/he.png",
  flash: "/grenades/flash.png",
  smoke: "/grenades/smoke.png",
  molotov: "/grenades/molotov.png",
  incendiary: "/grenades/incendiary.png",
};

/** Grenade HUD icon — a rendered PNG when we ship one, else color-coded SVG. */
export function grenadeIconSvg(id: string): string {
  const img = GRENADE_IMG_BY_ID[id];
  if (img) {
    return `<img class="hud-nic-img" src="${img}" alt="" aria-hidden="true" draggable="false" />`;
  }
  const body = GRENADE_SVGS[id] ?? `<circle cx="16" cy="18" r="9" fill="#8a8f96"/>`;
  return `<svg class="hud-nic" viewBox="0 0 32 32" aria-hidden="true">${body}</svg>`;
}

/* ── Team star — the center vitals emblem ─────────────────────────────── */

/** Five-point star inside a thin ring (uses `currentColor`). 28×28 viewBox. */
export function teamStarSvg(): string {
  return `<svg class="hud-star-svg" viewBox="0 0 28 28" fill="none" aria-hidden="true">
    <circle cx="14" cy="14" r="12" stroke="currentColor" stroke-width="1.6" opacity="0.85"/>
    <path d="M14 5.2 l2.6 5.27 5.82 .85 -4.21 4.1 .99 5.8 -5.2 -2.74 -5.2 2.74 .99 -5.8 -4.21 -4.1 5.82 -.85 Z" fill="currentColor"/>
  </svg>`;
}

/**
 * Hupper brand mark — the two-panel logo the web app uses (Header.tsx), here in
 * white for the HUD's dark center. Replaces the team star. Swap the `#f5efe6`
 * strokes for `currentColor` to team-tint it (CT blue / T gold) like the star.
 */
export function hupperLogoSvg(): string {
  return `<svg class="hud-logo-svg" viewBox="0 0 60 60" fill="none" aria-hidden="true">
    <path d="M6 6 L26 18 L26 42 L6 54 Z" stroke="#f5efe6" stroke-width="5" stroke-linejoin="miter"/>
    <path d="M54 6 L34 18 L34 42 L54 54 Z" stroke="#f5efe6" stroke-width="5" stroke-linejoin="miter"/>
  </svg>`;
}
